import React, { useEffect, useState } from "react";
import LibraryFormModal from "../components/LibraryFormModal";
import LibraryField, {
  libraryInputClass,
  librarySelectClass,
} from "../components/LibraryField";
import { ChevronDownIcon } from "../../../Icons";

const EMPTY = {
  insuranceName: "",
  category: "",
  insuranceCode: "",
  status: "Active",
};

const InsuranceFormModal = ({ isOpen, record, onClose, onSave }) => {
  const isEdit = Boolean(record?.id);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setSaving(false);
    setForm(
      record
        ? {
            insuranceName: record.insuranceName || "",
            category: record.category || "",
            insuranceCode: record.insuranceCode || "",
            status: record.status || "Active",
          }
        : EMPTY,
    );
  }, [isOpen, record]);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleSubmit = async () => {
    if (!form.insuranceName.trim()) {
      setErrors({ insuranceName: "Insurance name is required" });
      return;
    }
    setSaving(true);
    try {
      await onSave?.({
        insuranceName: form.insuranceName.trim(),
        category: form.category.trim(),
        insuranceCode: form.insuranceCode.trim(),
        status: form.status,
      });
      onClose?.();
    } catch (err) {
      setErrors({ form: err.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <LibraryFormModal
      isOpen={isOpen}
      title={isEdit ? "Edit Insurance" : "Create New Insurance"}
      onClose={onClose}
      onSubmit={handleSubmit}
      isSaving={saving}
      submitLabel={isEdit ? "Update" : "Create"}
    >
      {errors.form ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
          {errors.form}
        </p>
      ) : null}
      <LibraryField id="ins-name" label="Insurance Name" required error={errors.insuranceName}>
        <input
          id="ins-name"
          className={libraryInputClass(errors.insuranceName)}
          value={form.insuranceName}
          onChange={(e) => setField("insuranceName", e.target.value)}
          maxLength={255}
          autoFocus
        />
      </LibraryField>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <LibraryField id="ins-cat" label="Category">
          <input
            id="ins-cat"
            className={libraryInputClass()}
            value={form.category}
            onChange={(e) => setField("category", e.target.value)}
            maxLength={100}
          />
        </LibraryField>
        <LibraryField id="ins-code" label="Insurance Code">
          <input
            id="ins-code"
            className={libraryInputClass()}
            value={form.insuranceCode}
            onChange={(e) => setField("insuranceCode", e.target.value)}
            maxLength={50}
          />
        </LibraryField>
      </div>
      <LibraryField id="ins-status" label="Status">
        <div className="relative">
          <select
            id="ins-status"
            className={librarySelectClass()}
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <ChevronDownIcon
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
        </div>
      </LibraryField>
    </LibraryFormModal>
  );
};

export default InsuranceFormModal;
